import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClientPlatform from '@/apiClientPlatform'
import type {
  PlatformAdminsListResponse,
  PlatformCreateAdminBody,
  PlatformUpdateAdminRoleBody,
  PlatformDisableAdminBody,
  PlatformDisableAdminResponse,
  PlatformEnableAdminResponse,
} from '@/types/platform'

export const platformAdminsKey = ['platformAdmins'] as const

export const usePlatformAdminsQuery = () => {
  return useQuery({
    queryKey: platformAdminsKey,
    queryFn: async () => {
      const { data } = await apiClientPlatform.get<PlatformAdminsListResponse>(
        `/platform/admins`
      )
      return data
    },
  })
}

export const useCreatePlatformAdminMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PlatformCreateAdminBody) => {
      const { data } = await apiClientPlatform.post(`/platform/admins`, payload)
      return data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: platformAdminsKey })
    },
  })
}

export const useUpdatePlatformAdminRoleMutation = (adminId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PlatformUpdateAdminRoleBody) => {
      const { data } = await apiClientPlatform.patch(
        `/platform/admins/${adminId}/role`,
        payload
      )
      return data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: platformAdminsKey })
    },
  })
}

export const useDisablePlatformAdminMutation = (adminId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PlatformDisableAdminBody) => {
      const { data } =
        await apiClientPlatform.patch<PlatformDisableAdminResponse>(
          `/platform/admins/${adminId}/disable`,
          payload
        )
      return data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: platformAdminsKey })
    },
  })
}

export const useEnablePlatformAdminMutation = (adminId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } =
        await apiClientPlatform.patch<PlatformEnableAdminResponse>(
          `/platform/admins/${adminId}/enable`
        )
      return data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: platformAdminsKey })
    },
  })
}
